import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar, User, Eye, RefreshCw } from 'lucide-react';
import DRSTransactionEntry from './DRSTransactionEntry';
import apiClient from '../services/api';

const VIOLET     = '#667eea';
const VIOLET_DK  = '#764ba2';

const DRSList = () => {
  const [drsList,    setDRSList]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showForm,   setShowForm]   = useState(false);
  const [editData,   setEditData]   = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const p = JSON.parse(s);
      return p?.session || p;
    } catch { return null; }
  };
  const session = getSessionData();

  useEffect(() => { loadDRSList(); }, []);

  const loadDRSList = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/Drs.php');
      if (res.data.success) setDRSList(res.data.data || []);
      else throw new Error(res.data.message || 'Failed to load');
    } catch (err) { setError(err.response?.data?.message || err.message || 'Failed to load DRS list'); }
    finally { setLoading(false); }
  };

  const handleNew   = () => { setEditData(null); setShowForm(true); };
  const handleEdit  = (drs) => { setEditData(drs); setShowForm(true); };
  const handleClose = () => { setShowForm(false); setEditData(null); };

  const handleDelete = async (id, drsNo) => {
    if (!window.confirm(`Delete DRS ${drsNo}? This will also delete all detail lines.`)) return;
    try {
      const res = await apiClient.delete('/Drs.php', { data: { drsid: id } });
      if (res.data.success) { alert('Deleted successfully'); loadDRSList(); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Delete failed: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSave = async (formData) => {
    try {
      const isEdit = !!formData.drsid;
      const res = isEdit ? await apiClient.put('/Drs.php', formData) : await apiClient.post('/Drs.php', formData);
      if (res.data.success) {
        alert(isEdit ? `DRS updated: ${formData.drsno}` : `DRS created: ${res.data.data?.drsno}`);
        setShowForm(false); setEditData(null); loadDRSList();
      } else throw new Error(res.data.message);
    } catch (err) { throw new Error(err.response?.data?.message || err.message); }
  };

  if (!session?.companyid) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <h3 className="text-lg font-semibold text-gray-700">No Session Context</h3>
          <p className="text-sm text-gray-500">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filtered = drsList.filter(d =>
    d.drsno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.deliveredby_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.vehicle_regno?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const grandTotal = filtered.reduce((s, d) => s + (parseFloat(d.totalamount) || 0), 0);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        .drs-tr:hover td { background:#f9fafb; }
        @media print {
          .no-print { display:none !important; }
          .drs-actions-col { display:none !important; }
        }
        @media (max-width:1200px) {
          .drs-header { flex-direction:column; align-items:flex-start; gap:16px; }
          .drs-toolbar { flex-direction:column; align-items:stretch; }
          .drs-search { max-width:100% !important; }
        }
      `}</style>

      {/* Header */}
      <div className="drs-header flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0"
            style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
            <Truck size={28} />
          </div>
          <div>
            <h1 className="m-0 text-2xl font-bold text-gray-800">Daily Run Sheet (DRS)</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">Manage delivery runs and payment collections</p>
          </div>
        </div>
        <div className="flex gap-2.5">
          <button onClick={loadDRSList} title="Refresh"
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all duration-150">
            <RefreshCw size={18} />
          </button>
          <button onClick={handleNew}
            className="flex items-center gap-2 px-5 py-2.5 text-white border-0 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-lg"
            style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})`, boxShadow:`0 2px 4px rgba(102,126,234,0.3)` }}>
            <Plus size={20} /> New DRS
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="drs-toolbar flex justify-between items-center mb-6 gap-4">
        <div className="drs-search flex-1 max-w-[500px] relative flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2.5 transition-all duration-150 focus-within:border-violet-400 focus-within:shadow-[0_0_0_3px_rgba(102,126,234,0.1)]">
          <Search size={20} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by DRS No, Driver, or Vehicle..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none ml-2 text-sm text-gray-800 placeholder-gray-400 bg-transparent" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="bg-gray-100 border-0 p-1 rounded cursor-pointer flex items-center hover:bg-gray-200 transition-colors duration-100">
              <X size={16} className="text-gray-500" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
          <span className="px-2.5 py-1 rounded-xl text-[13px] font-semibold text-white"
            style={{ background: VIOLET }}>{filtered.length}</span>
          {filtered.length === 1 ? 'record' : 'records'}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <span className="w-10 h-10 border-4 border-gray-200 border-t-violet-500 rounded-full block" style={{ animation:'spin 0.8s linear infinite' }} />
          <p className="text-sm text-gray-500 m-0">Loading DRS records...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-5">
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-lg text-sm">
            <strong>Error:</strong> {error}
          </div>
          <button onClick={loadDRSList}
            className="flex items-center gap-2 px-5 py-2.5 text-white border-0 rounded-lg text-sm font-semibold cursor-pointer"
            style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <Truck size={48} className="text-gray-300" />
          <h3 className="m-0 text-lg font-semibold text-gray-700">No DRS records found</h3>
          <p className="m-0 text-sm text-gray-500 max-w-[400px]">
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first DRS'}
          </p>
          {!searchTerm && (
            <button onClick={handleNew}
              className="flex items-center gap-2 px-5 py-2.5 text-white border-0 rounded-lg text-sm font-semibold cursor-pointer mt-2"
              style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
              <Plus size={20} /> New DRS
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto shadow-sm"
          style={{ maxHeight:'calc(100vh - 280px)', overflowY:'auto' }}>
          <table className="w-full border-collapse text-sm" style={{ minWidth:900 }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                {['DRS No','DRS Date','Despatch Date','Driver','Vehicle','Items','Total','Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-[12px] font-semibold text-gray-500 uppercase tracking-wide ${h==='Total'?'text-right':'text-left'} ${h==='Actions'?'drs-actions-col w-[160px]':''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(drs => (
                <tr key={drs.drsid} className="drs-tr border-b border-gray-200 last:border-b-0">
                  <td className="px-4 py-3 font-mono font-bold" style={{ color: VIOLET }}>{drs.drsno}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-gray-500">
                      <Calendar size={14} className="text-gray-400" />
                      {new Date(drs.drsdate).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-gray-500">
                      <Calendar size={14} className="text-gray-400" />
                      {drs.despatchdate ? new Date(drs.despatchdate).toLocaleDateString() : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-start gap-2">
                      <User size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="font-medium text-gray-800">{drs.deliveredby_name || '—'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-start gap-2">
                      <Truck size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="font-medium text-gray-800">{drs.vehicle_regno || '—'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2.5 py-1 rounded-xl text-[13px] font-semibold bg-gray-100 text-gray-700">{drs.total_items || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-600 font-mono">
                    {parseFloat(drs.totalamount || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 drs-actions-col text-right whitespace-nowrap">
                    <button onClick={() => handleEdit(drs)} title="Edit"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md border cursor-pointer transition-all duration-150 hover:-translate-y-px hover:shadow ml-1 text-blue-500 border-blue-200 hover:bg-blue-50 hover:border-blue-500">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(drs.drsid, drs.drsno)} title="Delete"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md border cursor-pointer transition-all duration-150 hover:-translate-y-px hover:shadow ml-1 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-600">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                <td colSpan={6} className="px-4 py-3 text-right text-gray-600 font-semibold">Grand Total:</td>
                <td className="px-4 py-3 text-right text-emerald-600 font-mono text-[15px]">{grandTotal.toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {showForm && (
        <DRSTransactionEntry onClose={handleClose} onSave={handleSave} editData={editData} />
      )}
    </div>
  );
};

export default DRSList;